import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef } from 'react';

import { useContent } from '@/content/ContentContext';
import { Chapter, ChapterColumn } from '@/features/journey/Chapter';
import { MOTION_ALLOWED } from '@/motion/queries';
import { cx } from '@/ui/classNames';
import { smoothPath } from '@/ui/geometry/smoothPath';
import { Container } from '@/ui/Layout/Container';
import { RevealText } from '@/ui/Reveal/RevealText';
import { typography } from '@/ui/Typography/typography';

import styles from './Method.module.css';

/**
 * Chapitre 05, la méthode, face au trou noir « Cap ». Les quatre étapes
 * jalonnent une trajectoire courbée par sa gravité ; une sonde la parcourt au
 * rythme du défilement et allume chaque étape qu'elle atteint.
 */
export function Method() {
  const { method } = useContent();
  const sectionRef = useRef<HTMLElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const steps = stepsRef.current;
      if (!steps) return;
      const orbit = steps.querySelector<SVGPathElement>('[data-method="orbit"]');
      const trail = steps.querySelector<SVGPathElement>('[data-method="trail"]');
      const probe = steps.querySelector<SVGGElement>('[data-method="probe"]');
      const svg = steps.querySelector<SVGSVGElement>('svg');
      if (!orbit || !trail || !probe || !svg) return;

      // Le tracé passe par le centre de chaque pastille : on le recalcule à chaque mise en page.
      let length = 0;
      const trace = (): void => {
        const origin = steps.getBoundingClientRect();
        const nodes = gsap.utils.toArray<HTMLElement>('[data-method="node"]', steps);
        const points = nodes.map((node) => {
          const bounds = node.getBoundingClientRect();
          return {
            x: bounds.left + bounds.width / 2 - origin.left,
            y: bounds.top + bounds.height / 2 - origin.top,
          };
        });
        svg.setAttribute('viewBox', `0 0 ${origin.width} ${origin.height}`);
        const d = smoothPath(points);
        orbit.setAttribute('d', d);
        trail.setAttribute('d', d);
        length = trail.getTotalLength();
        trail.style.strokeDasharray = `${length}`;
      };
      trace();
      const layout = new ResizeObserver(trace);
      layout.observe(steps);

      const place = (progress: number): void => {
        trail.style.strokeDashoffset = `${length * (1 - progress)}`;
        const point = trail.getPointAtLength(length * progress);
        probe.setAttribute('transform', `translate(${point.x} ${point.y})`);
      };
      place(0);

      const media = gsap.matchMedia();
      media.add(MOTION_ALLOWED, () => {
        ScrollTrigger.create({
          trigger: steps,
          start: 'top 55%',
          end: 'bottom 55%',
          scrub: 0.6,
          onUpdate: (self) => {
            place(self.progress);
          },
        });
        gsap.utils.toArray<HTMLElement>('[data-method="step"]', steps).forEach((step) => {
          ScrollTrigger.create({
            trigger: step,
            start: 'top 60%',
            onToggle: (self) => {
              step.toggleAttribute('data-active', self.isActive || self.progress === 1);
            },
          });
        });
      });
      // Sans mouvement : trajectoire entière, toutes les étapes visibles.
      media.add('(prefers-reduced-motion: reduce)', () => {
        place(1);
      });

      return () => {
        layout.disconnect();
        media.revert();
      };
    },
    { scope: sectionRef },
  );

  return (
    <Chapter ref={sectionRef} chapter="method" id="methode" aria-labelledby="method-title">
      <Container>
        <ChapterColumn chapter="method">
          <RevealText id="method-title" className={cx(typography.heading, typography.h2)}>
            {method.title}
          </RevealText>
          <p className={typography.lead}>{method.lead}</p>

          <div ref={stepsRef} className={styles.steps}>
            <svg className={styles.trajectory} aria-hidden="true" preserveAspectRatio="none">
              <path className={styles.orbit} data-method="orbit" />
              <path className={styles.trail} data-method="trail" />
              <g data-method="probe">
                <circle className={styles.probeHalo} r="14" />
                <circle className={styles.probeCore} r="3.5" />
              </g>
            </svg>
            <ol>
              {method.steps.map((step, index) => (
                <li key={step.title} className={styles.step} data-method="step">
                  <span className={styles.node} aria-hidden="true" data-method="node">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className={styles.stepTitle}>{step.title}</h3>
                    <p className={styles.stepDescription}>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </ChapterColumn>
      </Container>
    </Chapter>
  );
}
